import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { authAwarePersistenceService } from './services/authAwarePersistenceService';
import { normalizeOrganizationId } from './utils/organizationAutoHeal';
import { ClinicalProvider } from './contexts/ClinicalContext';
import { OrgProvider } from './contexts/OrgContext';
import { NotificationProvider } from './components/NotificationSystem';
import AuthenticatedOrgGate from './components/auth/AuthenticatedOrgGate';

// Import authentication components
import LoginPage from './components/auth/LoginPage';
import PasswordSetup from './components/auth/PasswordSetup';
import authService from './services/authService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import rbacService from './services/rbacService';
import { workflowEventListener as workflowEventListenerInstance } from './services/workflowEventListener';

// Import role-specific dashboards
import ManagementDashboard from './components/dashboards/ManagementDashboard';
import ICPersonnelDashboard from './components/dashboards/ICPersonnelDashboard';
import AdministrationDashboard from './components/dashboards/AdministrationDashboard';
import ExecutionDashboard from './components/dashboards/ExecutionDashboard';
import PrescriptorsDashboard from './components/dashboards/PrescriptorsDashboard';
import QmsRecommendationsStandalone from './components/qms/QmsRecommendationsStandalone';
import DeviationsStandalone from './components/deviations/DeviationsStandalone';

// Sidebar shows administration-style menu; tree moved out of sidebar

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [userData, setUserData] = useState(null);
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const [structure, setStructure] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [initError, setInitError] = useState(null);

  // Check for password setup token in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const setupToken = urlParams.get('token');
    
    if (setupToken) {
      setShowPasswordSetup(true);
      return;
    }
  }, []);

  // Silent platform bootstrap check.
  // On a fresh, completely empty instance (no roles, no users), this seeds the
  // roles collection and creates the contact@zero-path.ro superadmin account.
  // On any already-initialized instance (staging, every live hospital silo),
  // the Cloud Function self-gates and returns permission-denied — expected,
  // ignored silently, no effect on UI.
  useEffect(() => {
    const runBootstrapCheck = async () => {
      try {
        const functions = getFunctions();
        const bootstrapPlatformFunction = httpsCallable(functions, 'bootstrapPlatform');
        const result = await bootstrapPlatformFunction();
        console.log('🌱 Platform bootstrap executed:', result.data?.message);
      } catch (error) {
        if (error.code === 'functions/permission-denied') {
          // Expected on every already-initialized instance — not an error
          console.log('Platform already initialized — bootstrap skipped');
        } else {
          console.error('Platform bootstrap check failed:', error);
        }
      }
    };

    runBootstrapCheck();
  }, []);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Initialize RBAC service with database data
        await rbacService.initialize();
        
        const currentUser = authService.getCurrentUser();
        
        if (currentUser) {
          try {
            const userProfile = await authService.getCurrentUserProfile();
            
            if (userProfile && userProfile.status === 'active') {
              const userRole = userProfile.role_id;
              
              try {
                // Validate user role exists in database
                const isValidRole = await rbacService.validateUserRole(userRole);
                
                if (isValidRole) {
                  // Get dashboard configuration from database
                  const dashboardConfig = await rbacService.getUserDashboard(userRole);
                  
                  setUserRole(userRole);
                  setUserData(userProfile);
                  setDashboardConfig(dashboardConfig);
                  setIsAuthenticated(true);
                  
                  console.log('Auto-login dashboard config loaded:', dashboardConfig);
                } else {
                  console.error(`Invalid user role: ${userRole}`);
                  await authService.logout();
                  setIsAuthenticated(false);
                }
              } catch (roleError) {
                console.error('Role validation error:', roleError);
                // During transition: allow login with fallback dashboard
                console.log('Using fallback dashboard configuration for role:', userRole);
                
                const fallbackConfig = {
                  dashboard: userRole === 'management' ? 'ManagementDashboard' : 'ExecutionDashboard',
                  permissions: [],
                  level: 0,
                  name: userRole,
                  description: ''
                };
                
                setUserRole(userRole);
                setUserData(userProfile);
                setDashboardConfig(fallbackConfig);
                setIsAuthenticated(true);
                
                console.log('Auto-login fallback dashboard config loaded:', fallbackConfig);
              }
            } else {
              await authService.logout();
              setIsAuthenticated(false);
            }
          } catch (profileError) {
            console.error('Profile check error:', profileError);
            await authService.logout();
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setInitError(error?.message || 'Failed to initialize. Check Firebase connection.');
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth().catch((err) => {
      console.error('Auth check failed:', err);
      setInitError(err?.message || 'Failed to initialize.');
      setAuthChecked(true);
    });
  }, []);

  // Bootstrap workflow event listener when user authenticates (singleton instance)
  useEffect(() => {
    if (!isAuthenticated) return;
    const firebaseUser = authService.getCurrentUser();
    if (!firebaseUser) return;

    const listener = workflowEventListenerInstance;
    if (listener.isInitialized) {
      // Already running — reload mappings in case workflows changed since last login
      listener.reloadMappings().catch(err =>
        console.error('WorkflowEventListener reloadMappings failed:', err)
      );
      return;
    }

    listener.initialize(firebaseUser.uid).catch(err =>
      console.error('WorkflowEventListener init failed:', err)
    );
    return () => listener.stop?.();
  }, [isAuthenticated]);

  const structureHydratingRef = useRef(false);

  // Keep persistence layer in sync with auth (saveData requires isAuthenticated)
  useEffect(() => {
    authAwarePersistenceService.setAuthenticationStatus(isAuthenticated);
  }, [isAuthenticated]);

  // Load org-scoped clinical structure.
  // Uses authAwarePersistenceService.loadForOrg → clinicalStructureService.getStructureByOrganization
  // (org-filtered hierarchical queries) instead of productionOptimizedService.loadMinimalEssential
  // which called FirebaseService.getAll() on 11 collections with no organizationId filter.
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const orgId = normalizeOrganizationId(
          userData?.organizationId ?? userData?.organization_id
        );
        console.log('⚡ Loading org-scoped structure for org:', orgId);

        if (isAuthenticated && orgId) {
          structureHydratingRef.current = true;
          const data = await authAwarePersistenceService.loadForOrg(orgId);
          setStructure(data);
          console.log('✅ Org-scoped structure loaded', {
            locations: data.locations?.length ?? 0,
            buildings: data.buildings?.length ?? 0,
            departments: data.departments?.length ?? 0,
            equipment: data.equipment?.length ?? 0
          });
          setTimeout(() => {
            structureHydratingRef.current = false;
          }, 1000);
        } else {
          // Not authenticated or org not resolved yet — set empty structure.
          // Dashboards use OrgContext.activeOrgId directly for all org-scoped queries.
          setStructure(authAwarePersistenceService.getEmptyStructure());
        }
      } catch (error) {
        console.error('❌ Error loading org structure:', error);
        setStructure(authAwarePersistenceService.getEmptyStructure());
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated, userData?.organizationId]);

  // Save only after explicit edits — never persist empty/hydrating structure (would hide existing Firestore data)
  useEffect(() => {
    if (structure && !isLoading && userRole && userRole !== 'execution') {
      const saveData = async () => {
        if (structureHydratingRef.current) {
          console.log('⏭️ Skipping save — structure still loading from server');
          return;
        }

        const clinicalKeys = ['locations', 'buildings', 'floors', 'departments', 'rooms', 'beds', 'equipment'];
        const hasClinicalData = clinicalKeys.some((k) => (structure[k]?.length ?? 0) > 0);
        if (!hasClinicalData) {
          console.log('⏭️ Skipping save — clinical structure is empty (data remains in Firestore)');
          return;
        }

        try {
          await authAwarePersistenceService.saveData(
            structure,
            normalizeOrganizationId(userData?.organizationId ?? userData?.organization_id)
          );
          console.log('💾 Data saved (authentication-aware)');
        } catch (error) {
          console.error('❌ Error saving data:', error);
        }
      };

      const timeoutId = setTimeout(saveData, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [structure, isLoading, userRole]);


  const handleLogin = async (loginUserData) => {
    try {
      console.log('Login successful:', loginUserData);
      
      const userRole = loginUserData.role_id;
      
      // Validate user role exists in database
      const isValidRole = await rbacService.validateUserRole(userRole);
      
      if (!isValidRole) {
        throw new Error(`Invalid user role: ${userRole}`);
      }
      
      // Get dashboard configuration from database
      const dashboardConfig = await rbacService.getUserDashboard(userRole);
      
      setUserRole(userRole);
      setUserData(loginUserData);
      setDashboardConfig(dashboardConfig);
      setIsAuthenticated(true);
      setAuthChecked(true);
      
      console.log('Dashboard config loaded:', dashboardConfig);
      
    } catch (error) {
      console.error('Login error:', error);
      // Handle invalid role gracefully
      alert(`Login failed: ${error.message}. Please contact administrator.`);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUserRole('');
      setUserData(null);
      setDashboardConfig(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handlePasswordSetupSuccess = async (userData) => {
    try {
      console.log('Password setup successful:', userData);
      
      // After password setup, the user needs to actually log in
      // The Cloud Function returns user data, but we need to authenticate
      // Show a message and redirect to login
      alert(`Password setup complete! Please log in with your email (${userData.email}) and new password.`);
      
      setShowPasswordSetup(false);
      setAuthChecked(true);
      setIsAuthenticated(false); // Force user to login page
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, '/');
    } catch (error) {
      console.error('Password setup success error:', error);
    }
  };

  // Called by Step1Organization (via SetupWizard) after a superadmin account
  // creates its first organization. Updates userData in place so OrgContext
  // re-resolves activeOrgId immediately — no logout, no page reload.
  const handleOrganizationAttached = (organizationId) => {
    const orgId = normalizeOrganizationId(organizationId);
    console.log('Organization attached to superadmin account:', orgId);
    setUserData(prev => prev ? { ...prev, organizationId: orgId } : prev);
  };

  // Show password setup page if token is present
  if (showPasswordSetup) {
    return (
      <PasswordSetup onSuccess={handlePasswordSetupSuccess} />
    );
  }


  // Show loading while auth is being checked
  if (!authChecked) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <h2>Initializing...</h2>
      </div>
    );
  }

  // CONFIGURATION REQUIRED: RBAC/database failed to load (per .cursorrules: no fallback data, signal to user)
  if (initError) {
    return (
      <div className="loading-container" style={{ maxWidth: '480px', margin: '2rem auto', textAlign: 'center' }}>
        <div role="alert" className="configuration-required-block" style={{
          padding: '1.5rem',
          border: '2px solid #f59e0b',
          borderRadius: '8px',
          background: '#fffbeb'
        }}>
          <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>🔧 CONFIGURATION REQUIRED</p>
          <p style={{ marginBottom: '0.5rem' }}>Unable to load roles from database. Check Firebase connection and ensure the <code>roles</code> collection exists.</p>
          <p style={{ fontSize: '0.875rem', color: '#92400e' }}>{initError}</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <LoginPage onSuccess={handleLogin} />
      </>
    );
  }

  // Dynamic dashboard routing based on database configuration
  const renderDashboard = () => {
    if (!dashboardConfig) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h2>Loading Dashboard Configuration</h2>
          <p>Please wait while we load your dashboard...</p>
        </div>
      );
    }

    try {
      // Dynamic import based on database configuration
      const DashboardComponent = getDashboardComponent(dashboardConfig.dashboard);

      const forceSetupWizard =
        userRole === 'superadmin' &&
        (!structure?.organizations || structure.organizations.length === 0);
      
      return (
        <DashboardComponent
          onLogout={handleLogout}
          userRole={userRole}
          userData={userData}
          dashboardConfig={dashboardConfig}
          structure={structure}
          setStructure={setStructure}
          forceSetupWizard={forceSetupWizard}
          onOrganizationAttached={handleOrganizationAttached}
        />
      );
    } catch (error) {
      console.error('Dashboard loading error:', error);
      return (
        <div className="error-container">
          <h2>Dashboard Not Available</h2>
          <p>Your dashboard configuration could not be loaded.</p>
          <p>Error: {error.message}</p>
          <button onClick={handleLogout} className="btn btn-primary">
            Logout
          </button>
        </div>
      );
    }
  };

  // Get dashboard component dynamically
  const getDashboardComponent = (dashboardName) => {
    switch (dashboardName) {
      case 'ManagementDashboard':
        return ManagementDashboard;
      case 'ICPersonnelDashboard':
        return ICPersonnelDashboard;
      case 'AdministrationDashboard':
        return AdministrationDashboard;
      case 'ExecutionDashboard':
        return ExecutionDashboard;
      case 'PrescriptorsDashboard':
        return PrescriptorsDashboard;
      default:
        throw new Error(`Unknown dashboard: ${dashboardName}`);
    }
  };

  // Show the main app if authenticated
  if (isAuthenticated) {
    // Show loading state while data is being loaded
    if (isLoading || !structure) {
      return (
        <NotificationProvider>
          <ClinicalProvider>
            <OrgProvider user={userData}>
              <div className="app-layout">
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <h2>Ultra-Fast Loading</h2>
                  <p>Loading from cache or minimal essential data...</p>
                  <small style={{ marginTop: '10px', opacity: 0.7 }}>
                    ⚡ Production optimized: &lt;1 second (cache) or ~2 seconds (fresh)
                  </small>
                </div>
              </div>
            </OrgProvider>
          </ClinicalProvider>
        </NotificationProvider>
      );
    }

    // Role-specific dashboard + deep links (e.g. /qms/recommendations)
    return (
      <NotificationProvider>
        <ClinicalProvider>
          <OrgProvider user={userData}>
            <BrowserRouter>
              <AuthenticatedOrgGate userRole={userRole}>
                <Routes>
                  <Route
                    path="/qms/recommendations"
                    element={
                      <QmsRecommendationsStandalone userData={userData} userRole={userRole} />
                    }
                  />
                  <Route
                    path="/deviations"
                    element={
                      <DeviationsStandalone userData={userData} userRole={userRole} />
                    }
                  />
                  <Route path="/" element={renderDashboard()} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AuthenticatedOrgGate>
            </BrowserRouter>
          </OrgProvider>
        </ClinicalProvider>
      </NotificationProvider>
    );
  }

  // Return null if no condition matches (shouldn't happen)
  return null;
}

export default App;
